import { getAccessToken } from "../lib/supabase";

/**
 * Base URL of the edge functions.
 *
 * Defaults to the linked Supabase project; override with `VITE_JUDGE_URL` when
 * running the functions locally (the Deno dev router serves `/functions/v1/...`
 * on port 54321).
 */
export const API_BASE = (
  import.meta.env.VITE_JUDGE_URL ??
    `${import.meta.env.VITE_SUPABASE_URL ?? ""}/functions/v1`
).replace(/\/$/, "");

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

/** `fetch` wrapper that attaches the current Supabase access token. */
export async function authedFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new ApiError(401, "unauthorized", "Please sign in again.");

  const headers = new Headers(init.headers);
  headers.set("authorization", `Bearer ${token}`);
  if (init.body !== undefined && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

/** Turn a non-OK response into a typed error, reading the JSON body if present. */
export async function readApiError(response: Response): Promise<ApiError> {
  let code = "error";
  let message = `Request failed (${response.status})`;
  try {
    const body = (await response.json()) as {
      error?: string;
      message?: string;
    };
    if (body.error) code = body.error;
    if (body.message) message = body.message;
  } catch {
    // Non-JSON error body; keep the status-based fallback message.
  }
  return new ApiError(response.status, code, message);
}
